/**
 * AuthContext.tsx
 *
 * Provides authentication state (user, session, profile, loading) to the
 * entire application via React context.
 *
 * Initialization strategy (Supabase v2):
 *   We deliberately separate the two Supabase auth mechanisms:
 *
 *   1. `getSession()` — called once on mount to resolve the initial session
 *      stored in localStorage. This is the ONLY path that sets loading=false.
 *
 *   2. `onAuthStateChange` — handles SUBSEQUENT auth events (SIGNED_IN,
 *      SIGNED_OUT, TOKEN_REFRESHED, etc.). We explicitly skip the
 *      INITIAL_SESSION event here to avoid a race condition where both paths
 *      run fetchProfile concurrently and the second INSERT attempt fails with
 *      a 23505 duplicate-key error that is silently swallowed.
 *
 * Profile auto-creation:
 *   On first login (no profile row yet), fetchProfile inserts a new profile.
 *   lisgrapci6@gmail.com is seeded with the super_admin role.
 *   If the row already exists (concurrent insert race → 23505), we re-fetch
 *   rather than failing silently.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import {
  getNativeSession,
  clearNativeSession,
  nativeSignIn,
  nativeSignOut,
  seedSupabaseSession,
  clearSupabaseSession,
} from '../lib/authNative';
import {
  apiConfigured,
  authConfigured,
  clearSignedOutReason,
  getCurrentWorkspaceId,
  onSessionExpired,
  setCurrentWorkspaceId,
} from '../lib/apiClient';
import { queryClient } from '../lib/queryClient';
import { UserProfile } from '../types';
/* No DEFAULT_WORKSPACE_ID import any more, deliberately. This file is where the workspace is
 * DECIDED — from the profile — so importing the fallback constant here would be circular
 * reasoning. See `publishWorkspace`. */

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  /**
   * True once a profile fetch has FINISHED — whether it produced a profile or
   * bailed out. Distinct from `loading`, which only covers session resolution
   * and is force-cleared by a 5-second safety valve that can fire while
   * `fetchProfile` is still in flight.
   *
   * Pages that need a profile to scope a query must gate on THIS, not on
   * `loading`: `profileSettled && !profile` is a genuine failure, whereas
   * `!loading && !profile` is also true during normal slow resolution — and
   * telling a user "your profile could not be loaded" while it is still loading
   * is exactly the kind of false claim the rest of this codebase is removing.
   */
  profileSettled: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]       = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileSettled, setProfileSettled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fix 0.1: `ensureEmployeeRecord` was removed. The profile IS the person /
  // HR record now (the `employees` table was consolidated into `profiles`), so
  // login no longer needs to create or claim a separate employee row — the
  // profile created below in `fetchProfile` is the single person record.

  /**
   * Fetches the user's profile row. If it doesn't exist (PGRST116 — no rows
   * returned by .single()), creates one. If the insert races with another
   * concurrent call and hits a unique-violation (23505), re-fetches instead.
   *
   * Role is read from `profiles.role` (the source of truth for this
   * single-tenant database). A legacy `workspace_members` lookup remains only
   * as a fallback for deployments that store role there; a missing/failed
   * lookup never downgrades an existing `profiles.role`.
   */
  /**
   * Publish the signed-in person's ACTUAL workspace so the rest of the app can scope to it.
   *
   * ── THIS IS THE ONE LINE MULTI-TENANCY TURNS ON ─────────────────────────────────────
   * Both branches below used to call `setCurrentWorkspaceId(DEFAULT_WORKSPACE_ID)` — the
   * hardcoded constant. The holder was already here, already set on profile load and already
   * cleared on logout; it was simply being told that everyone belongs to company one. The
   * database has never believed that: every workspace-scoped policy compares against
   * `current_user_workspace()`, which is literally
   * `select workspace_id from profiles where id = auth.uid()`. This makes the client read the
   * same row, so the two can no longer disagree.
   *
   * ── NULL IS A REAL, CORRECT ANSWER — DO NOT DEFAULT IT ──────────────────────────────
   * `handle_new_user` creates profiles with `workspace_id` NULL on purpose (migration 0115):
   * a fresh signup genuinely has no workspace until an admin assigns one. Publishing a
   * fallback here would put an unprovisioned person into somebody else's company, which is
   * the exact cross-tenant leak 0115's own comments were written to prevent. Callers must
   * handle null by NOT QUERYING, never by guessing — `ProtectedRoute` already holds such a
   * user at `AwaitingProvisioning`.
   *
   * ── AND THE CACHE GOES WITH IT ─────────────────────────────────────────────────────
   * `setWorkspace` below empties the React Query cache whenever the answer CHANGES.
   * React Query keeps every fetched list in memory for five minutes after nothing is using
   * it, and signing out does not reload the page — so without this, signing out and signing
   * back in as somebody from a DIFFERENT company would render the previous company's
   * contacts, deals and invoices straight from cache. The right rows, for the wrong person,
   * in the same browser.
   *
   * It is done HERE, in one place, rather than by naming the workspace in every query key.
   * Far more keys exist than the handful that carry a workspace, and a key somebody forgets
   * to parameterise fails silently; a cache emptied on identity change cannot be forgotten.
   */
  const setWorkspace = (next: string | null): void => {
    const previous = getCurrentWorkspaceId();
    setCurrentWorkspaceId(next);
    if (previous !== next) queryClient.clear();
  };

  /** The signed-out state: nobody, nothing to wait for, and no cached lists. */
  const clearSignedInState = (): void => {
    clearSupabaseSession();
    setUser(null);
    setSession(null);
    setProfile(null);
    setProfileSettled(true);
    setWorkspace(null);
  };

  const publishWorkspace = (profileData: Record<string, unknown>): void => {
    const ws = profileData.workspace_id;
    setWorkspace(typeof ws === 'string' && ws ? ws : null);
  };

  const fetchProfile = async (userId: string, email?: string): Promise<void> => {
    try {
      let profileData: Record<string, unknown> | null = null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (data) {
        profileData = data as Record<string, unknown>;
      } else if (error?.code === 'PGRST116' && email) {
        // First login — create the profile WITHOUT a role column (role lives in workspace_members)
        const { data: inserted, error: insertErr } = await supabase
          .from('profiles')
          .insert({
            id:        userId,
            email,
            full_name: email.split('@')[0],
          })
          .select()
          .single();

        if (inserted) {
          profileData = inserted as Record<string, unknown>;
        } else if (insertErr?.code === '23505') {
          // 23505 = another concurrent call already inserted — just re-fetch
          // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
          const { data: existing } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
          if (existing) profileData = existing as Record<string, unknown>;
        } else {
          console.warn('Profile insert failed:', insertErr);
          return;
        }
      } else if (error) {
        // Any other error (table missing, RLS, network) — log and continue
        console.warn('Could not fetch profile (schema may not be applied yet):', error);
        return;
      }

      if (!profileData) return;

      // Fix 0.1: no separate employee row to ensure — the profile above IS the
      // person/HR record. HR pages (leave / attendance / resignation) now key
      // on profiles.id directly.

      // ── Resolve role ─────────────────────────────────────────────────────────
      // `profiles.role` is the SOURCE OF TRUTH. This database stores the role
      // directly on the profile and has no `workspace_members` table.
      //
      // BUGFIX: the previous version ALWAYS derived the role from a
      // `workspace_members` query and, when that query failed (e.g. the table
      // doesn't exist — error 42P01 / PGRST205, which is NOT PGRST116), fell
      // through to a hardcoded `role: 'employee'`, silently downgrading a real
      // super_admin. We now trust `profiles.role` first and only consult
      // `workspace_members` as a legacy fallback when the profile has no role —
      // and never let a missing table/row downgrade an existing role.
      const profileRole = (profileData.role as string | null | undefined) ?? null;

      if (profileRole) {
        setProfile({ ...profileData, role: profileRole } as UserProfile);
        publishWorkspace(profileData);
        return;
      }

      /* Profile has no role. There is no client-side bootstrap, deliberately.
       *
       * WHAT WAS HERE, AND WHY IT IS GONE. A `workspace_members` fallback used to sit
       * in this branch: read a role from that table, and if no row existed, INSERT one
       * defaulting to `super_admin` when the email matched a hardcoded address, then
       * write that role back onto profiles.
       *
       * It could never execute. `workspace_members` does not exist in this database
       * (verified 2026-07-19 and again 2026-07-21), so the read fails with 42P01 —
       * NOT the PGRST116 "no rows" the insert branch tested for — and control fell
       * straight to the graceful branch below. Dead code, but dead code that granted
       * super_admin off a string comparison: if anyone ever created a table by that
       * name, it would have silently reanimated as a live escalation path.
       *
       * It also cannot come back. Migration 0113 puts a BEFORE UPDATE trigger on
       * profiles that refuses any authenticated write to `role`, so a client-side
       * self-promotion is now denied by the database regardless of what the client
       * asks for.
       *
       * PROVISIONING: the first super_admin in a new workspace is set by the
       * provisioning migration under service_role, which 0113 exempts. There is no
       * in-app path and there should not be one — a self-service route to super_admin
       * is the hole 0113 exists to close. See PULSEHQ_STATUS.md section 6.
       *
       * DO NOT DOWNGRADE here: keep whatever the profile had, and fall to 'employee'
       * only when there is genuinely nothing. */
      setProfile({ ...profileData, role: (profileData.role as string) || 'employee' } as UserProfile);
      publishWorkspace(profileData);
    } catch (err) {
      console.warn('fetchProfile threw unexpectedly:', err);
    }
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id, user.email);
  };

  useEffect(() => {
    // Guard: skip state updates if this effect has been cleaned up
    let mounted = true;

    // Safety valve — never stay stuck on loading spinner more than 5 s
    const timeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 5000);

    // ── Path 1: Resolve the session that is already in localStorage ──────────
    // This is the sole owner of the loading=false transition.
    const initializeAuth = async () => {
      try {
        // Native path first: our own session in localStorage (workspace cut over).
        if (apiConfigured()) {
          // A pass that ran out while the tab was closed is renewed by apiClient
          // before the first call goes out (`renewExpiredSessionAtBoot`), so the
          // profile read below never leaves with it.
          const native = getNativeSession();
          if (native) {
            const nativeUser = { id: native.user.id, email: native.user.email } as User;
            setUser(nativeUser);
            setSession({
              user: nativeUser,
              access_token: native.access_token,
              refresh_token: native.refresh_token,
            } as Session);
            // Seed supabase-js with our native JWT so data queries carry it.
            seedSupabaseSession(native);
            await fetchProfile(native.user.id, native.user.email ?? undefined);
            if (mounted) setProfileSettled(true);
            return;
          }
        }
        // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // fetchProfile resolves through every one of its internal bail-outs,
          // so awaiting it is enough to know the attempt is over.
          await fetchProfile(session.user.id, session.user.email);
        }
        if (mounted) setProfileSettled(true);
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (mounted) setProfileSettled(true);
      } finally {
        if (mounted) {
          clearTimeout(timeout);
          setLoading(false);
        }
      }
    };

    // ── Path 1b: the refresh token died (apiClient's renewal was refused) ────
    // apiClient has already cleared the stored session and left the log-in page
    // its sentence. Clearing the user here is what sends ProtectedRoute to /login,
    // instead of leaving the app half-loaded as "User" with every call failing.
    const stopExpiryWatch = onSessionExpired(() => {
      if (mounted) clearSignedInState();
    });

    initializeAuth();

    // ── Path 2: React to future auth events (login, logout, token refresh) ──
    // INITIAL_SESSION is intentionally skipped here — it is already handled
    // by getSession() above. Processing it twice caused a race condition where
    // both paths tried to INSERT the same profile simultaneously, the second
    // INSERT failed silently, and the app could get stuck with profile=null.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      // NOTE: this callback is intentionally NON-async and never awaits a
      // Supabase call. supabase-js holds an internal auth lock while this
      // callback runs; any `await supabase.from(...)` inside it tries to
      // re-acquire that same lock and deadlocks (the next query anywhere hangs
      // with no request issued). This is Supabase's documented "no async
      // Supabase calls inside onAuthStateChange" hazard, triggered by
      // background TOKEN_REFRESHED events. We keep only synchronous state
      // updates inline and defer fetchProfile out of the callback tick via
      // setTimeout(..., 0) so the auth lock releases first.
      (event: string, session: Session | null) => {
        if (!mounted) return;

        // Let initializeAuth handle the startup session — don't double-process
        if (event === 'INITIAL_SESSION') return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Defer the Supabase-touching profile fetch out of the auth-lock
          // tick. Re-check `mounted` inside the timer — mount state may change
          // before it fires.
          // A new sign-in means the previous settle no longer describes this
          // user — reopen the window so pages wait rather than claiming failure.
          setProfileSettled(false);
          setTimeout(() => {
            if (!mounted) return;
            fetchProfile(session.user.id, session.user.email)
              .catch(err => console.warn('Profile fetch failed on auth state change:', err))
              .finally(() => { if (mounted) setProfileSettled(true); });
          }, 0);
        } else {
          // Plain state setter, no Supabase call — safe to keep inline.
          setProfile(null);
          setProfileSettled(true); // signed out: there is nothing to wait for
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(timeout);
      stopExpiryWatch();
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, adding dep would cause re-render loop
  }, []);

  /** Signs in via our own backend when it's configured, otherwise
   *  Supabase. The onAuthStateChange SIGNED_IN event (Supabase path) or the
   *  native session handler below sets user/session/profile state. */
  const signIn = async (email: string, password: string) => {
    if (authConfigured()) {
      const res = await nativeSignIn(email, password);
      if (res.error) return res;
      clearSignedOutReason();
      if (res.session) {
        // Publish the native session to React state immediately — the Supabase
        // onAuthStateChange path won't fire for our own tokens.
        const nativeUser = { id: res.session.user.id, email: res.session.user.email } as User;
        setUser(nativeUser);
        setSession({ user: nativeUser, access_token: res.session.access_token, refresh_token: res.session.refresh_token } as Session);
        setProfileSettled(false);
        setTimeout(() => {
          fetchProfile(res.session!.user.id, res.session!.user.email ?? undefined)
            .catch(err => console.warn('Profile fetch failed on native sign-in:', err))
            .finally(() => setProfileSettled(true));
        }, 0);
      }
      return { error: null };
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  /** Signs the user out and clears local profile state immediately. */
  const signOut = async () => {
    if (apiConfigured()) {
      await nativeSignOut();
      clearNativeSession();
      clearSignedInState();
      return;
    }
    await supabase.auth.signOut();
    setProfile(null);
    // Clear the cutover-gate workspace pointer so a subsequent unauthed
    // visit can't accidentally hit a backend route the new user isn't
    // opted into yet. Goes through setWorkspace so the cached lists of the
    // person signing out do not survive into the next person's session.
    setWorkspace(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, profileSettled, signIn, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * The signed-in person's workspace, for any REACT component that needs to scope a query.
 * Plain non-React modules use `getCurrentWorkspaceId()` from lib/apiClient instead — the two
 * read the same value, published by `publishWorkspace` above.
 *
 * It lives here rather than beside `useWorkspaceCurrency` in ModuleContext (where the plan
 * first put it) for one reason: the workspace comes from the profile, and the profile lives
 * here. Putting it in ModuleContext would have meant ModuleContext importing AuthContext to
 * re-export a value it does not own.
 *
 * ── NULL MEANS "DO NOT QUERY YET", AND IT IS NOT THE SAME AS "NO WORKSPACE" ─────────
 * Two different situations both read as null, and a caller that conflates them will either
 * flash the wrong data or tell a provisioned user they have no company:
 *
 *   settled === false  → still resolving. Render your loading state. DO NOT query.
 *   settled === true  and  workspaceId === null  → genuinely unprovisioned.
 *
 * That is the same distinction `profileSettled` was added for, so this hook returns it rather
 * than making every caller remember to ask for it separately.
 */
export function useWorkspaceId(): { workspaceId: string | null; settled: boolean } {
  const { profile, profileSettled } = useAuth();
  const ws = profile?.workspace_id;
  return {
    workspaceId: typeof ws === 'string' && ws ? ws : null,
    settled: profileSettled,
  };
}
